import { Helmet } from 'react-helmet-async';

const SITE_NAME = "LabComplyx";
const SITE_URL = "https://labcomplyx.com";
const DEFAULT_OG_IMAGE = `https://media.base44.com/images/public/69b19cbac2089eb9a798a568/1a24f87cd_image.png`;
const DEFAULT_DESCRIPTION = "LabComplyx provides CLIA compliance consulting, CAP inspection readiness, and CLIA gap audits for independent clinical labs — without big-firm fees. Book a free gap audit today.";
const DEFAULT_AUTHOR = "LabComplyx";

// ── GEO 2026: Triple-schema @graph foundation ──────────────────────────────
// Research-validated (2026): FAQPage + Article + HowTo stacked via @graph
// produces 1.8x more AI citations than single-schema implementations.
// Person/Author schema anchors E-E-A-T signals for AI trust scoring.
// SpeakableSpecification signals voice-assistant extraction readiness.
// Source: OptimizeGEO.ai, GenOptima, DirectiveConsulting (2026 studies)
const FOUNDER_PERSON = {
  "@type": "Person",
  "@id": `${SITE_URL}/#founder`,
  "name": "Irvin Palmer",
  "jobTitle": "Founder & CEO, LabComplyx",
  "url": `${SITE_URL}/About`,
  "sameAs": [
    "https://www.linkedin.com/in/irvinpalmer",
    `${SITE_URL}/About`
  ],
  "knowsAbout": [
    "CLIA Compliance", "CAP Accreditation", "ICP-MS Instrumentation",
    "Analytical Chemistry", "GxP Compliance", "21 CFR Part 11",
    "AI-Assisted QA Documentation", "ALCOA+", "ISO 15189"
  ],
  "hasCredential": [
    { "@type": "EducationalOccupationalCredential", "name": "10+ years analytical chemistry and GxP compliance expertise" },
    { "@type": "EducationalOccupationalCredential", "name": "ICP-MS instrumentation specialist" }
  ]
};

const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      // ── Entity 1: Organization ────────────────────────────────────────────
      "@type": ["ProfessionalService", "SoftwareApplication"],
      "@id": `${SITE_URL}/#organization`,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "name": "LabComplyx",
      "alternateName": ["LabComplyx AI Compliance", "LabComplyx QA Platform"],
      "url": SITE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": DEFAULT_OG_IMAGE,
        "width": 1200,
        "height": 630
      },
      "image": DEFAULT_OG_IMAGE,
      "priceRange": "$$",
      "email": "hello@labcomplyx.com",
      // GEO 2026: Definitional sentence — CMU research confirms first 150 tokens
      // carry disproportionate weight in LLM retrieval summarization pipelines.
      "description": "LabComplyx is an AI-assisted compliance documentation platform built for small and medium CLIA- and CAP-regulated clinical laboratories. It automates GxP document drafting, CAPA workflows, IQ/OQ/PQ validation protocols, and audit-trail generation — replacing manual QA paperwork without big-firm consulting fees.",
      "foundingDate": "2024",
      "founder": { "@id": `${SITE_URL}/#founder` },
      "employee": { "@id": `${SITE_URL}/#founder` },
      "areaServed": [
        { "@type": "State", "name": "North Carolina" },
        { "@type": "State", "name": "South Carolina" },
        { "@type": "Country", "name": "United States" }
      ],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "5540 Centerview Dr Ste 204",
        "addressLocality": "Raleigh",
        "addressRegion": "NC",
        "postalCode": "27606-8012",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 35.7796,
        "longitude": -78.6382
      },
      "sameAs": [
        "https://www.linkedin.com/company/labcomplyx",
        `${SITE_URL}/AITransparencyStatement`,
        `${SITE_URL}/governance-faq`
      ],
      "serviceType": [
        "AI Governance for Regulated Labs",
        "CLIA Compliance Consulting",
        "CAP Inspection Readiness",
        "CLIA Gap Audit",
        "AI QA Documentation Platform",
        "CAPA Documentation Software",
        "IQ OQ PQ Validation Documentation",
        "GxP Compliance Consulting",
        "FDA QMSR Compliance",
        "FDA CSA Compliance",
        "Lab SOP Management",
        "Personnel Competency Tracking",
        "Deviation Management",
        "21 CFR Part 11 Audit Trail"
      ],
      "knowsAbout": [
        "CLIA Compliance", "CAP Accreditation", "CLIA Gap Analysis",
        "CAP Inspection Preparation", "Lab Personnel Competency",
        "CLIA QC Requirements", "FDA CSA Compliance", "FDA QMSR",
        "EU AI Act", "ALCOA+", "GDocP", "CAPA Documentation",
        "Validation Protocols", "IQ OQ PQ", "Computer Software Assurance",
        "21 CFR Part 11", "GxP Compliance", "ISO 15189", "ISO 17025",
        "AI Governance", "Human-in-the-Loop Review", "AI Audit Trails",
        "Regulatory Traceability", "ICH E6(R3)", "QMSR", "CLIA Waiver",
        "Clinical Laboratory Improvement Amendments"
      ]
    },
    {
      // ── Entity 2: Founder / Author (E-E-A-T anchor) ───────────────────────
      ...FOUNDER_PERSON
    },
    {
      // ── Entity 3: Website ─────────────────────────────────────────────────
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      "url": SITE_URL,
      "name": "LabComplyx",
      "description": "AI-assisted CLIA and CAP compliance documentation platform for regulated laboratories.",
      "publisher": { "@id": `${SITE_URL}/#organization` },
      "potentialAction": {
        "@type": "SearchAction",
        "target": { "@type": "EntryPoint", "urlTemplate": `${SITE_URL}/CapacityQA?q={search_term_string}` },
        "query-input": "required name=search_term_string"
      }
    }
  ]
};

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogImageUrl,
  ogUrl,
  schema,
  noIndex = false,
  // Optional: pass "article", "service", "faq" etc. to auto-generate page schema
  pageType,
  // For blog/article pages
  datePublished,
  dateModified,
  author,
  // For breadcrumb
  breadcrumbs,
}) {
  // Auto-apply breadcrumbs to SEO component for schema.org support
  const fullTitle = title
    ? (title.toLowerCase().includes('labcomplyx') ? title : `${title} | ${SITE_NAME}`)
    : `CLIA & CAP Compliance Consulting for Labs | ${SITE_NAME}`;
  const metaDescription = description || DEFAULT_DESCRIPTION;
  const currentOgImage = ogImageUrl || DEFAULT_OG_IMAGE;
  const currentUrl = ogUrl || canonical || SITE_URL;

  // Build WebPage schema to wrap all pages.
  // Note: "faq" pages render plain WebPage here — FAQPage (with required mainEntity)
  // is emitted separately by the FAQSchema component.
  const webPageSchemaType = pageType === "article" ? "Article" : "WebPage";
  // GEO 2026: Per-page schema links back to global @graph entities via @id
  // so AI crawlers can traverse the full entity graph from any entry point.
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": webPageSchemaType,
    "@id": `${currentUrl}#webpage`,
    "url": currentUrl,
    "name": fullTitle,
    ...(pageType === "article" && { "headline": title || fullTitle }),
    "description": metaDescription,
    "isPartOf": { "@id": `${SITE_URL}/#website` },
    "publisher": { "@id": `${SITE_URL}/#organization` },
    ...(datePublished && { "datePublished": datePublished }),
    ...(dateModified && { "dateModified": dateModified }),
    // GEO 2026: Author linked to global Person entity for AI trust scoring
    "author": author
      ? { "@type": "Person", "name": author, "@id": `${SITE_URL}/#founder` }
      : { "@id": `${SITE_URL}/#founder` },
    // Reviewer = same expert signals double E-E-A-T weight to AI crawlers.
    // schema.org only defines reviewedBy on WebPage (not Article), so only attach it there.
    ...(webPageSchemaType === "WebPage" && { "reviewedBy": { "@id": `${SITE_URL}/#founder` } }),
    ...(pageType === "article" && {
      "image": currentOgImage,
      "mainEntityOfPage": { "@type": "WebPage", "@id": currentUrl },
      // GEO 2026: speakable on article pages to flag extractable content
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": [".geo-tldr", "h1", "h2", ".answer-block"]
      }
    }),
  };

  // Build BreadcrumbList schema if breadcrumbs provided
  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "name": crumb.name,
      "item": crumb.url,
    })),
  } : null;

  const jsonLd = schema || ORG_SCHEMA;
  // Always emit a canonical — use the canonical prop, fallback to ogUrl, then construct from pathname
  const canonicalHref = canonical || ogUrl || (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : SITE_URL);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalHref} />
      {noIndex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
      }

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={currentOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME} — ${title || 'CLIA & CAP Compliance Consulting for Labs'}`} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@labcomplyx" />
      <meta name="twitter:creator" content="@labcomplyx" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={currentOgImage} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} — ${title || 'CLIA & CAP Compliance Consulting for Labs'}`} />

      {/* Author for blog/article pages */}
      {author && <meta name="author" content={author} />}
      {datePublished && <meta property="article:published_time" content={datePublished} />}
      {dateModified && <meta property="article:modified_time" content={dateModified} />}

      {/* JSON-LD: org/page schema */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}
    </Helmet>
  );
}